import { useEffect, useState } from 'react';
import api from '../../api/axios';
import S3ImagePicker from '../components/S3ImagePicker';
import RichEditor from '../components/RichEditor';

const EMPTY = {
  title: '',
  description: '',
  event_date: '',
  venue: '',
  status: 'upcoming',
  category: 'Annual',
  badge: 'Upcoming',
  photoAlbumUrl: '',
  registerUrl: '',
};

export default function EventsManager() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [formKey, setFormKey] = useState('init');
  const [imageFile, setImageFile] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/events').then(r => setEvents(r.data || [])).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handle = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setImageFile(null);
    setImageUrl('');
    setError('');
    setFormKey('new-' + Date.now());
    setShowForm(true);
  };

  const openEdit = ev => {
    setEditing(ev.id);
    setForm({
      title: ev.title,
      description: ev.description || '',
      event_date: ev.event_date?.substring(0, 10) || '',
      venue: ev.venue || '',
      status: ev.status || 'upcoming',
      category: ev.category || 'Annual',
      badge: ev.badge || 'Upcoming',
      photoAlbumUrl: ev.photoAlbumUrl || '',
      registerUrl: ev.registerUrl || '',
    });
    setImageFile(null);
    setImageUrl(ev.image_url || '');
    setError('');
    setFormKey(ev.id);
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);
      else if (imageUrl) fd.append('image_url', imageUrl);
      if (editing) await api.put(`/events/${editing}`, fd);
      else await api.post('/events', fd);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    await api.delete(`/events/${id}`);
    load();
  };

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  return (
    <div>
      {showPicker && (
        <S3ImagePicker
          onSelect={url => { setImageUrl(url); setImageFile(null); setShowPicker(false); }}
          onClose={() => setShowPicker(false)}
        />
      )}

      <div className="d-flex align-items-center justify-content-between mb-4">
        <div><h4 className="fw-bold mb-0">Events</h4><p className="text-muted small mb-0">Manage upcoming and past events</p></div>
        <button className="btn btn-primary rounded-pill px-4" onClick={openAdd}><i className="fas fa-plus me-2"></i>Add Event</button>
      </div>

      {showForm && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-body p-4">
            <h5 className="fw-bold mb-4">{editing ? 'Edit Event' : 'Add Event'}</h5>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <form onSubmit={save}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label small">Title *</label>
                  <input name="title" value={form.title} onChange={handle} required className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Date *</label>
                  <input name="event_date" type="date" value={form.event_date} onChange={handle} required className="form-control" />
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Status</label>
                  <select name="status" value={form.status} onChange={handle} className="form-select">
                    <option value="upcoming">Upcoming</option>
                    <option value="past">Past</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small">Badge Label</label>
                  <input name="badge" value={form.badge} onChange={handle} className="form-control" placeholder="e.g. Annual, Upcoming" />
                </div>
                <div className="col-12">
                  <label className="form-label small">Venue</label>
                  <input name="venue" value={form.venue} onChange={handle} className="form-control" />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-bold">Description</label>
                  <RichEditor
                    key={formKey}
                    defaultValue={form.description}
                    onChange={html => setForm(f => ({ ...f, description: html }))}
                    placeholder="Describe the event…"
                    minHeight={200}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">
                    <i className="fas fa-ticket-alt text-danger me-1"></i>
                    Register Button URL <span className="text-muted">(optional — shown as "Register Now" button for upcoming events)</span>
                  </label>
                  <input
                    name="registerUrl"
                    value={form.registerUrl}
                    onChange={handle}
                    className="form-control"
                    placeholder="https://forms.gle/… or https://eventbrite.com/…"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">
                    <i className="fab fa-facebook text-primary me-1"></i>
                    Facebook Photo Album URL <span className="text-muted">(optional — shown as "See Photos" button)</span>
                  </label>
                  <input
                    name="photoAlbumUrl"
                    value={form.photoAlbumUrl}
                    onChange={handle}
                    className="form-control"
                    placeholder="https://www.facebook.com/media/set/?set=a.…"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small">Event Image</label>
                  <div className="d-flex gap-2 align-items-start flex-wrap">
                    <div className="flex-grow-1">
                      <input type="file" accept="image/*" className="form-control" onChange={e => { setImageFile(e.target.files[0]); setImageUrl(''); }} />
                    </div>
                    <button type="button" className="btn btn-outline-secondary rounded-pill px-3" onClick={() => setShowPicker(true)}>
                      <i className="fas fa-folder-open me-2"></i>Select from Media
                    </button>
                  </div>
                  {imageUrl && !imageFile && (
                    <div className="mt-2 d-flex align-items-center gap-2">
                      <img src={imageUrl} alt="preview" style={{ height: '60px', width: '80px', objectFit: 'cover', borderRadius: '4px' }} />
                      <span className="text-muted small text-truncate" style={{ maxWidth: '300px' }}>{imageUrl}</span>
                      <button type="button" className="btn btn-sm btn-outline-danger rounded-pill px-2" onClick={() => setImageUrl('')}>Remove</button>
                    </div>
                  )}
                </div>
              </div>
              <div className="d-flex gap-2 mt-4">
                <button type="submit" disabled={saving} className="btn btn-primary rounded-pill px-4">{saving ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
      ) : (
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr><th>Title</th><th>Date</th><th>Venue</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {events.length === 0 && <tr><td colSpan="5" className="text-center text-muted py-4">No events yet.</td></tr>}
                {events.map(ev => (
                  <tr key={ev.id}>
                    <td>
                      {ev.title}
                      {ev.photoAlbumUrl && <i className="fab fa-facebook text-primary ms-2" title="Has photo album"></i>}
                    </td>
                    <td>{fmtDate(ev.event_date)}</td>
                    <td>{ev.venue || '—'}</td>
                    <td><span className={`badge ${ev.status === 'upcoming' ? 'badge-soft-success' : 'badge-soft-secondary'}`}>{ev.status}</span></td>
                    <td>
                      <button className="btn btn-sm btn-outline-primary me-2" onClick={() => openEdit(ev)}>Edit</button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => remove(ev.id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
